'use client';

/**
 * Rewards: badges, the shop, and the avatar editor.
 *
 * Everything purchasable is cosmetic — nothing here buys progress or a scoring
 * advantage. Locked badges show their progress bar rather than being hidden,
 * because a visible "3 of 7" is motivating and a mystery is not.
 */
import React, { useState } from 'react';
import { Check, Lock } from 'lucide-react';
import { useKids } from '../context/KidsProvider';
import { ACHIEVEMENTS, TIER_ORDER } from '../data/achievements';
import { SHOP_CATEGORIES, SHOP_ITEMS } from '../data/shop';
import { rankFor } from '../lib/metrics';
import Avatar, { AVATAR_OPTIONS, HAIR_COLORS, SKIN_TONES } from './Avatar';
import { CoinBadge, ProgressRail, LoadingBlock } from './ui';
import type { ShopItem } from '../types';

const TIER_COLOR: Record<string, string> = {
  bronze: '--k-tangerine',
  silver: '--k-lagoon',
  gold: '--k-mango',
  legend: '--k-grape',
};

type Tab = 'badges' | 'shop' | 'avatar';

export default function Rewards() {
  const { profile, hydrated } = useKids();
  const [tab, setTab] = useState<Tab>('badges');

  if (!hydrated || !profile) {
    return <LoadingBlock height={400} />;
  }

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div
          role="tablist"
          aria-label="Rewards sections"
          className="inline-flex p-1 rounded-full"
          style={{ background: 'var(--muted)' }}
        >
          {(
            [
              ['badges', '🏅 Badges'],
              ['shop', '🛍️ Shop'],
              ['avatar', '🧑 My look'],
            ] as [Tab, string][]
          ).map(([id, label]) => (
            <button
              key={id}
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              className="k-btn k-btn-sm"
              style={
                tab === id
                  ? {
                      background: 'var(--card)',
                      color: 'var(--accent)',
                      boxShadow: 'var(--k-shadow-sm)',
                    }
                  : {
                      background: 'transparent',
                      color: 'var(--muted-foreground)',
                      boxShadow: 'none',
                    }
              }
            >
              {label}
            </button>
          ))}
        </div>
        <CoinBadge coins={profile.coins} />
      </div>

      {tab === 'badges' && <Badges />}
      {tab === 'shop' && <Shop />}
      {tab === 'avatar' && <AvatarEditor />}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── badges ── */

function Badges() {
  const { profile } = useKids();
  if (!profile) return null;

  const sorted = [...ACHIEVEMENTS].sort((a, b) => {
    const aEarned = Boolean(profile.achievements[a.id]);
    const bEarned = Boolean(profile.achievements[b.id]);
    if (aEarned !== bEarned) return aEarned ? -1 : 1;
    return TIER_ORDER[a.tier] - TIER_ORDER[b.tier];
  });
  const earnedCount = Object.keys(profile.achievements).length;

  return (
    <section>
      <p className="text-sm text-muted-foreground mb-4">
        <strong className="text-foreground">{earnedCount}</strong> of {ACHIEVEMENTS.length} badges
        earned. None of them are for speed alone — every one rewards accuracy, consistency or
        finishing what you started.
      </p>

      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 k-stagger">
        {sorted.map((badge, i) => {
          const earnedAt = profile.achievements[badge.id];
          const progress = badge.progress?.(profile) ?? 0;
          return (
            <li
              key={badge.id}
              className={`k-card p-4 ${earnedAt ? '' : 'k-locked'}`}
              style={{
                ['--k-i' as string]: i,
                borderColor: earnedAt ? `var(${TIER_COLOR[badge.tier]})` : undefined,
              }}
            >
              <div className="flex items-start gap-3">
                <span
                  className="grid place-items-center rounded-2xl shrink-0"
                  style={{
                    width: 48,
                    height: 48,
                    fontSize: 26,
                    background: earnedAt
                      ? `color-mix(in srgb, var(${TIER_COLOR[badge.tier]}) 18%, transparent)`
                      : 'var(--muted)',
                  }}
                  aria-hidden="true"
                >
                  {earnedAt ? badge.emoji : <Lock size={19} />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-extrabold leading-tight">{badge.name}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">{badge.description}</p>
                  {earnedAt ? (
                    <p
                      className="text-xs font-extrabold mt-2"
                      style={{ color: `var(${TIER_COLOR[badge.tier]})` }}
                    >
                      ✓ Earned {new Date(earnedAt).toLocaleDateString()}
                    </p>
                  ) : (
                    badge.progress && (
                      <div className="mt-2">
                        <ProgressRail value={progress * 100} max={100} />
                        <p className="text-xs text-muted-foreground mt-1">
                          {Math.round(progress * 100)}% of the way there
                        </p>
                      </div>
                    )
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────── shop ── */

function Shop() {
  const { profile, buyItem, equipItem } = useKids();
  const [message, setMessage] = useState<string | null>(null);
  if (!profile) return null;

  const rank = rankFor(profile.xp).rank.level;

  const isEquipped = (item: ShopItem) => {
    switch (item.category) {
      case 'keyboard':
        return profile.equipped.keyboardSkin === item.value;
      case 'title':
        return profile.equipped.title === item.value;
      case 'pet':
        return profile.avatar.pet === item.value;
      default:
        return (
          profile.avatar.hair === item.value ||
          profile.avatar.accessory === item.value ||
          profile.avatar.background === item.value
        );
    }
  };

  const handle = (item: ShopItem) => {
    if (profile.unlocked.includes(item.id) || item.cost === 0) {
      equipItem(item);
      setMessage(null);
      return;
    }
    const result = buyItem(item);
    setMessage(result.ok ? `Unlocked ${item.name}!` : (result.reason ?? 'Could not unlock that.'));
  };

  return (
    <section>
      {message && (
        <p
          role="status"
          className="k-card p-3 mb-4 text-sm font-extrabold"
          style={{ borderColor: 'var(--accent)' }}
        >
          {message}
        </p>
      )}

      {SHOP_CATEGORIES.map((category) => (
        <div key={category.id} className="mb-8">
          <h2 className="text-lg mb-3">
            <span aria-hidden="true">{category.emoji}</span> {category.label}
          </h2>
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {SHOP_ITEMS.filter((item) => item.category === category.id).map((item) => {
              const owned = profile.unlocked.includes(item.id) || item.cost === 0;
              const equipped = isEquipped(item);
              const rankLocked = Boolean(item.requiresRank && rank < item.requiresRank);
              const affordable = profile.coins >= item.cost;

              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => handle(item)}
                    disabled={rankLocked || (!owned && !affordable)}
                    className="k-card-interactive w-full p-4 text-center disabled:cursor-not-allowed"
                    style={equipped ? { borderColor: 'var(--accent)' } : undefined}
                    aria-pressed={equipped}
                  >
                    <span style={{ fontSize: 32 }} aria-hidden="true">
                      {item.emoji}
                    </span>
                    <span className="block font-extrabold mt-1.5 text-sm">{item.name}</span>
                    <span className="block text-xs text-muted-foreground mt-0.5 min-h-[2rem]">
                      {item.description}
                    </span>
                    <span className="block mt-2">
                      {rankLocked ? (
                        <span className="k-pill" style={{ background: 'var(--muted)' }}>
                          <Lock size={12} /> Rank {item.requiresRank}
                        </span>
                      ) : equipped ? (
                        <span
                          className="k-pill"
                          style={{ background: 'var(--accent)', color: '#fff' }}
                        >
                          <Check size={13} /> Wearing
                        </span>
                      ) : owned ? (
                        <span className="k-pill" style={{ background: 'var(--muted)' }}>
                          Tap to wear
                        </span>
                      ) : (
                        <span
                          className="k-pill"
                          style={{
                            background: affordable ? 'var(--k-mango-soft)' : 'var(--muted)',
                            color: affordable ? 'var(--k-mango)' : 'var(--faint)',
                          }}
                        >
                          🪙 {item.cost}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
}

/* ───────────────────────────────────────────────────────── avatar editor ── */

function AvatarEditor() {
  const { profile, updateAvatar } = useKids();
  if (!profile) return null;

  const owned = (value: string) =>
    SHOP_ITEMS.some((i) => i.value === value && !profile.unlocked.includes(i.id) && i.cost > 0)
      ? false
      : true;

  return (
    <section className="grid gap-6 lg:grid-cols-[220px_1fr]">
      <div className="k-card p-5 text-center h-fit lg:sticky lg:top-24">
        <Avatar config={profile.avatar} size={150} animated />
        <p className="font-extrabold mt-3">
          {profile.name} {profile.equipped.title}
        </p>
      </div>

      <div className="grid gap-5">
        <Swatches
          label="Skin"
          values={SKIN_TONES}
          current={profile.avatar.skin}
          onPick={(skin) => updateAvatar({ skin })}
        />
        <Swatches
          label="Hair colour"
          values={HAIR_COLORS}
          current={profile.avatar.hairColor}
          onPick={(hairColor) => updateAvatar({ hairColor })}
        />

        <Chooser
          label="Hair style"
          options={AVATAR_OPTIONS.hair}
          current={profile.avatar.hair}
          isOwned={owned}
          onPick={(hair) => updateAvatar({ hair })}
        />
        <Chooser
          label="Eyes"
          options={AVATAR_OPTIONS.eyes}
          current={profile.avatar.eyes}
          isOwned={() => true}
          onPick={(eyes) => updateAvatar({ eyes })}
        />
        <Chooser
          label="Face shape"
          options={AVATAR_OPTIONS.body}
          current={profile.avatar.body}
          isOwned={() => true}
          onPick={(body) => updateAvatar({ body })}
        />
        <Chooser
          label="Accessory"
          options={AVATAR_OPTIONS.accessory}
          current={profile.avatar.accessory}
          isOwned={owned}
          onPick={(accessory) => updateAvatar({ accessory })}
        />
        <Chooser
          label="Background"
          options={AVATAR_OPTIONS.background}
          current={profile.avatar.background}
          isOwned={owned}
          onPick={(background) => updateAvatar({ background })}
        />

        <p className="text-sm text-muted-foreground">
          Greyed-out options are still locked — unlock them in the shop with coins you earn from
          lessons.
        </p>
      </div>
    </section>
  );
}

function Swatches({
  label,
  values,
  current,
  onPick,
}: {
  label: string;
  values: readonly string[];
  current: string;
  onPick: (value: string) => void;
}) {
  return (
    <fieldset>
      <legend className="k-eyebrow mb-2">{label}</legend>
      <div className="flex flex-wrap gap-2">
        {values.map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => onPick(value)}
            aria-label={`${label}: ${value}`}
            aria-pressed={current === value}
            className="rounded-full transition-transform hover:scale-110"
            style={{
              width: 40,
              height: 40,
              background: value,
              border: `3px solid ${current === value ? 'var(--accent)' : 'var(--border-strong)'}`,
            }}
          />
        ))}
      </div>
    </fieldset>
  );
}

function Chooser({
  label,
  options,
  current,
  isOwned,
  onPick,
}: {
  label: string;
  options: readonly string[];
  current: string;
  isOwned: (value: string) => boolean;
  onPick: (value: string) => void;
}) {
  return (
    <fieldset>
      <legend className="k-eyebrow mb-2">{label}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map((value) => {
          const unlocked = isOwned(value);
          return (
            <button
              key={value}
              type="button"
              disabled={!unlocked}
              onClick={() => onPick(value)}
              aria-pressed={current === value}
              className={`k-btn k-btn-secondary k-btn-sm capitalize ${unlocked ? '' : 'k-locked'}`}
              style={
                current === value
                  ? { borderColor: 'var(--accent)', color: 'var(--accent)' }
                  : undefined
              }
            >
              {!unlocked && <Lock size={13} aria-hidden="true" />}
              {value}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
